import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Loader2 } from "lucide-react";
import {
  authHeaders,
  ROLE_LABEL,
  canManageUsers,
} from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["viewer", "editor", "owner"];

/**
 * Owner-only team management panel.
 * Lists existing admin users, lets owners invite (create) new ones,
 * change roles and remove accounts.
 *
 * Backend endpoints (all require_owner):
 *   GET    /api/admin/users
 *   POST   /api/admin/users
 *   PATCH  /api/admin/users/{id}/role
 *   DELETE /api/admin/users/{id}
 */
export default function TeamManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "editor",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: authHeaders(),
      });
      setUsers(data || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load team — owner role required.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManageUsers(currentUser)) load();
  }, [currentUser]);

  if (!canManageUsers(currentUser)) {
    return (
      <div
        data-testid="team-restricted-card"
        className="border border-amber-400/30 bg-zinc-950 p-8"
      >
        <Shield className="h-7 w-7 text-amber-400" />
        <h3 className="mt-4 font-display font-bold text-2xl">
          Owner access required
        </h3>
        <p className="mt-3 text-zinc-300 text-sm leading-relaxed max-w-md">
          Only Owner-level admins can invite new team members or change roles.
          Ask an Owner on the team to upgrade your access if you need it.
        </p>
      </div>
    );
  }

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, form, { headers: authHeaders() });
      toast.success(`Invited ${form.email}.`);
      setForm({ email: "", name: "", password: "", role: "editor" });
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to invite user");
    } finally {
      setCreating(false);
    }
  };

  const onRoleChange = async (user, role) => {
    if (role === user.role) return;
    try {
      await axios.patch(
        `${API}/admin/users/${user.id}/role`,
        { role },
        { headers: authHeaders() },
      );
      toast.success(`${user.email} → ${ROLE_LABEL[role]}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    }
  };

  const onDelete = async (user) => {
    if (
      !window.confirm(
        `Remove ${user.email}? They'll lose admin access immediately.`,
      )
    )
      return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: authHeaders(),
      });
      toast.success("Team member removed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div data-testid="team-management" className="space-y-10">
      {/* Invite form */}
      <div className="border border-amber-400/30 bg-zinc-950 p-7">
        <h3 className="font-display font-bold text-xl flex items-center gap-3">
          <UserPlus className="h-5 w-5 text-amber-400" /> Invite team member
        </h3>
        <p className="mt-2 text-xs uppercase tracking-[0.3em] text-zinc-500">
          Owners can add Editors (full content access) or Viewers (read-only)
        </p>
        <form
          onSubmit={onCreate}
          className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="team-invite-form"
        >
          <Field
            label="Email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            type="email"
            required
            testId="team-invite-email"
          />
          <Field
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            testId="team-invite-name"
          />
          <Field
            label="Temporary password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            type="password"
            required
            testId="team-invite-password"
          />
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">
              Role
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="team-invite-role"
              className="mt-2 w-full bg-black border border-white/10 px-3 py-3 text-white focus:outline-none focus:border-amber-400"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="team-invite-submit"
            className="sm:col-span-2 lg:col-span-4 inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-6 py-3 font-bold uppercase tracking-[0.25em] text-xs"
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Inviting…
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" /> Add team member
              </>
            )}
          </button>
        </form>
        <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
          Share the temporary password securely (1Password, signed email).
          They can change it from{" "}
          <span className="text-amber-400">Profile → Change password</span>{" "}
          after their first login.
        </p>
      </div>

      {/* Team list */}
      <div className="border border-white/5 bg-zinc-950 overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
              <th className="text-left px-5 py-4">Member</th>
              <th className="text-left px-5 py-4">Role</th>
              <th className="text-left px-5 py-4">Last login</th>
              <th className="text-right px-5 py-4">Remove</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-zinc-500">
                  <Loader2 className="h-5 w-5 animate-spin inline-block" />
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-zinc-500">
                  No team members yet.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                data-testid={`team-row-${u.id}`}
                className="border-b border-white/5 last:border-none"
              >
                <td className="px-5 py-4">
                  <p className="font-display font-bold">{u.name}</p>
                  <p className="text-xs text-zinc-400">{u.email}</p>
                </td>
                <td className="px-5 py-4">
                  <select
                    value={u.role}
                    onChange={(e) => onRoleChange(u, e.target.value)}
                    data-testid={`team-role-${u.id}`}
                    className="bg-black border border-white/10 px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold text-amber-400 focus:outline-none focus:border-amber-400"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-5 py-4 text-zinc-400 text-xs">
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleString()
                    : "Never"}
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(u)}
                    data-testid={`team-delete-${u.id}`}
                    disabled={u.id === currentUser?.id}
                    title={
                      u.id === currentUser?.id
                        ? "You can't remove yourself"
                        : "Remove member"
                    }
                    className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed text-xs uppercase tracking-[0.25em] font-bold"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="mt-2 w-full bg-black border border-white/10 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
      />
    </div>
  );
}
